import { defineStore } from 'pinia';
import { ref } from 'vue';

import { logoutAuth, refreshToken } from '@/api/login';
import type { PortalLogin } from '@/api/login/types';
import type { PortalUser, PortalUserMember, PortalUserType } from '@/api/user/types';
import { memberDetail, userDetail } from '@/api/user/user';
import { clearAllUserInfo } from '@/common/functions';
import { isNullOrUnDef } from '@/common/is';
import { identifyUser } from '@/utils/analytics';
// 用户登录对象存储对象
export const usePortalLoginStore = defineStore(
  'user',
  () => {
    // 定义用户信息
    const portalLogin = ref<PortalLogin | null>(null);
    // 是否有未读消息（预留字段）
    const _hasNotificationMessage = ref<boolean>(false);
    // 设置用户信息
    const setPortalLogin = (val: PortalLogin) => {
      portalLogin.value = val;

      const user = val?.user;
      if (user?.id) {
        identifyUser({
          userId: String(user.id),
          nickname: user.nickname,
          userType: user.type,
        });
        // login_success 不在此处打：由 open-member / share-vip 在 mpLogin 成功落库后显式上报。
        // 刷新用户详情也会进 setPortalLogin，若在这里打会误计或与登录路径重复。
      }
    };

    // 更新用户详情数据
    const updateUserDetail = (user: PortalUser) => {
      portalLogin.value.user = user;
      const value = portalLogin.value;
      setPortalLogin(value);
    };

    // 删除登录对象信息
    const removePortalLogin = () => {
      portalLogin.value = null;
    };

    // 检查是否未登录
    const isNotLogged = (): boolean => {
      return isNullOrUnDef(portalLogin.value);
    };
    // 是否登录
    const isLogged = (): boolean => {
      return !isNotLogged();
    };
    /**
     * 是否是推荐官
     * @returns {boolean}
     */
    const isRecommendationOfficer = (): boolean => {
      if (isNullOrUnDef(portalLogin.value)) {
        return false;
      }
      if (portalLogin.value.user.inviteCode) {
        return true;
      }
      return false;
    };

    /**
     * 退出登录 并 删除用户信息
     */
    const logout = async () => {
      logoutAuth();
      clearAllUserInfo();
    };

    // 刷新登录凭证
    const refreshTokenIfNeeded = async () => {
      if (isNotLogged()) {
        throw new Error('用户未登录，无法刷新登录凭证');
      }
      try {
        await refreshToken();
      } catch (error) {
        throw error;
      }
    };

    // 更新用户类型
    const modifyUserType = (type: PortalUserType) => {
      if (isLogged()) {
        // 更新用户类型
        updateUserDetail({
          ...portalLogin.value.user,
          type,
        });
      }
    };
    // 更新本地用户信息
    const updateUserDetailIfNeeded = async (): Promise<void> => {
      if (isLogged()) {
        try {
          const user = await userDetail();
          // 数据请求成功，刷新本地对象
          if (user) {
            updateUserDetail(user);
          }
        } catch (error) {
          console.error('更新用户信息失败:', error);
        }
      }
    };

    return {
      portalLogin,
      setPortalLogin,
      logout,
      isLogged,
      isNotLogged,
      removePortalLogin,
      refreshTokenIfNeeded,
      updateUserDetailIfNeeded,
      isRecommendationOfficer,
      updateUserDetail,
      modifyUserType,
    };
  },
  {
    persist: true,
  },
);

/// 用户会员信息
export const usePortalUserMemberStore = defineStore(
  'userMember',
  () => {
    const userMember = ref<PortalUserMember | null>(null);

    // 设置用户会员信息
    const setUserMember = (val: PortalUserMember) => {
      userMember.value = val;
    };
    const removeUserMember = () => {
      userMember.value = null;
    };
    const isMember = (): boolean => {
      if (usePortalLoginStore().isNotLogged()) {
        removeUserMember();
        return false;
      }
      if (isNullOrUnDef(userMember.value)) {
        return false;
      }
      const member = userMember.value.isMember;
      // 会员是否过期，如果没取到值，默认过期
      const expires = userMember.value.isExpires === false;
      return member && expires;
    };

    /// 刷新用户会员信息
    const updateUserMemberIfNeeded = async (): Promise<void> => {
      // 只有用户登录后才需要更新会员信息
      if (usePortalLoginStore().isNotLogged()) {
        return;
      }
      try {
        const res = await memberDetail();
        if (res) {
          setUserMember(res);
        }
      } catch (error) {
        console.error('更新用户会员信息失败:', error);
      }
    };

    /// 是否为会员过期
    const isMemberExpires = (): boolean => {
      if (usePortalLoginStore().isNotLogged()) {
        removeUserMember();
        return false;
      }
      if (isNullOrUnDef(userMember.value)) {
        return false;
      }
      const member = userMember.value.isMember;
      if (!member) {
        return false;
      }
      // 会员是否过期，如果没取到值，默认过期
      const expires = userMember.value.isExpires === false;
      return expires;
    };

    return {
      userMember,
      setUserMember,
      isMember,
      isMemberExpires,
      updateUserMemberIfNeeded,
      removeUserMember,
    };
  },
  {
    persist: true,
  },
);
